import { useMemo, useState, useEffect, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ChevronDown,
  FastForward,
  ListMusic,
  ListPlus,
  ListStart,
  MoreHorizontal,
  Music2,
  Pause,
  Play,
  Repeat,
  Repeat1,
  Rewind,
  Shuffle,
  SkipBack,
  SkipForward,
  Square,
  Trash2,
  Volume1,
  Volume2,
  VolumeX,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useGuildRights } from '@/lib/access';
import { useLiveStream } from '@/lib/ws';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { ActionMenu } from '@/components/ActionMenu';
import {
  Cover,
  DragHandle,
  DropLine,
  IconButton,
  SongSearch,
  SpotifyBadge,
  formatTime,
  useDragOrder,
  type Song,
} from '@/components/music';
import { AddToPlaylistDialog, PlaylistsTab, fromQueueSong, type SongInput } from './MusicPlaylists';
import { RadioTab } from './MusicRadio';
import { HistoryTab } from './MusicHistory';

type LoopMode = 'off' | 'queue' | 'song';

interface MusicState {
  type: 'music-state';
  guildId: string;
  playing: boolean;
  isPaused: boolean;
  /** Server volume in % (saved, applies to every song). */
  volume: number;
  /** Muted for the rest of the session (the saved volume stays). */
  muted: boolean;
  voiceChannelId: string | null;
  /** Position in the current song when the bot sent this state. */
  positionSec: number | null;
  durationSec: number | null;
  seekable: boolean;
  isRadio: boolean;
  /** Session modes — until stop. `nextUid`: the song shuffle drew as next. */
  shuffle?: boolean;
  loop?: LoopMode;
  nextUid?: string | null;
  historyCount?: number;
  queue: Song[];
  updatedAt: string;
}

type QueueEdit = { op: 'jump' | 'next' | 'remove'; uid: string } | { op: 'move'; uid: string; toIndex: number };

/** Ten-second steps, same as the buttons under the player message in Discord. */
const SEEK_STEP = 10;

const LOOP_LABEL: Record<LoopMode, string> = {
  off: 'Wiederholen: aus',
  queue: 'Wiederholen: Warteschlange',
  song: 'Wiederholen: Song',
};

type Tab = 'now' | 'queue' | 'playlists' | 'radio' | 'history';

/**
 * Music page, laid out like Spotify: tabs (Jetzt läuft, Warteschlange,
 * Playlists, Radio, Verlauf) above a player bar that stays at the bottom. Phones get a
 * mini bar that opens the full player. Live state: `music-state` events of
 * this guild. Playlists: pages/MusicPlaylists.tsx.
 */
export function Music() {
  const { guildId } = useParams<{ guildId: string }>();
  const { messages } = useLiveStream(200);

  // Latest state that matches this guild.
  const latest = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      const p = m.payload as { type?: string; guildId?: string } | undefined;
      if (
        m.channel === 'bot:event' &&
        p?.type === 'music-state' &&
        p.guildId === guildId
      ) {
        return m.payload as MusicState;
      }
    }
    return null;
  }, [messages, guildId]);

  // Saved server volume for the slider before anything plays; the request
  // also makes the bot publish its current state right away.
  const saved = useQuery({
    queryKey: ['music', guildId],
    queryFn: () => api<{ volume: number }>(`/api/guilds/${guildId}/music`),
  });
  const [localVolume, setLocalVolume] = useState(100);
  useEffect(() => {
    const v = latest?.volume ?? saved.data?.volume;
    if (v) setLocalVolume(v);
  }, [latest?.volume, saved.data?.volume]);

  // When the state arrived — the progress bar runs on from there.
  const [receivedAt, setReceivedAt] = useState(() => Date.now());
  useEffect(() => setReceivedAt(Date.now()), [latest]);
  const position = usePosition(latest, receivedAt);

  const doAction = (name: string, body: object = {}) =>
    api(`/api/actions/music-${name}`, {
      method: 'POST',
      body: JSON.stringify({ guildId, ...body }),
    });

  const [voiceChannelId, setVoiceChannelId] = useState<string | null>(null);
  const playMut = useMutation({
    mutationFn: (query: string) =>
      doAction('play', { query, voiceChannelId: voiceChannelId ?? undefined }),
  });
  // Radio tab: shown as "startet…" until the bot's state has the station.
  const [radioStarting, setRadioStarting] = useState<string | null>(null);
  const radioMut = useMutation({
    mutationFn: (stationId: string) =>
      doAction('radio', { stationId, voiceChannelId: voiceChannelId ?? undefined }),
    onMutate: (stationId) => setRadioStarting(stationId),
    onError: () => setRadioStarting(null),
  });

  const pauseMut = useMutation({ mutationFn: () => doAction('pause') });
  const resumeMut = useMutation({ mutationFn: () => doAction('resume') });
  const skipMut = useMutation({ mutationFn: () => doAction('skip') });
  const backMut = useMutation({ mutationFn: () => doAction('back') });
  const stopMut = useMutation({ mutationFn: () => doAction('stop') });
  const shuffleMut = useMutation({ mutationFn: () => doAction('shuffle') });
  const loopMut = useMutation({ mutationFn: () => doAction('loop') });
  const queueMut = useMutation({ mutationFn: (edit: QueueEdit) => doAction('queue', edit) });
  const volumeMut = useMutation({
    mutationFn: (v: number) => doAction('volume', { volume: v }),
  });
  const muteMut = useMutation({
    mutationFn: (muted: boolean) => doAction('mute', { muted }),
  });
  const seekMut = useMutation({
    mutationFn: (b: { position: number } | { delta: number }) => doAction('seek', b),
  });

  const playlistMut = useMutation({
    mutationFn: (playlistId: string) =>
      doAction('playlist', { playlistId, voiceChannelId: voiceChannelId ?? undefined }),
    onSuccess: () => setTab('queue'),
  });

  // Controls need the edit right; own playlists only the view right
  // (ServerLayout leaves this page out of its read-only switch).
  const { can } = useGuildRights(guildId);
  const canControl = can('music', 'edit');

  const hasSession = !!latest && latest.queue.length > 0;
  const current = latest?.queue[0] ?? null;
  const canPlay = hasSession || !!voiceChannelId;
  const playingStationId = latest?.isRadio ? (current?.stationId ?? null) : null;
  useEffect(() => {
    if (radioStarting && playingStationId === radioStarting) setRadioStarting(null);
  }, [playingStationId, radioStarting]);
  // A station that doesn't answer: the bot reports it (toast) and stops.
  useEffect(() => {
    if (!radioStarting) return;
    const t = setTimeout(() => setRadioStarting(null), 20_000);
    return () => clearTimeout(t);
  }, [radioStarting]);
  const [tab, setTab] = useState<Tab>('now');
  const [expanded, setExpanded] = useState(false);
  const [toPlaylist, setToPlaylist] = useState<SongInput | null>(null);
  useEffect(() => {
    if (!hasSession) setExpanded(false);
  }, [hasSession]);

  const controlError = [pauseMut, resumeMut, skipMut, backMut, stopMut, shuffleMut, loopMut, queueMut, seekMut, muteMut, volumeMut].find(
    (m) => m.error
  )?.error as Error | undefined;

  const controls: Controls | null =
    latest && hasSession
      ? {
          state: latest,
          pos: position.pos,
          toggle: () => (latest.isPaused ? resumeMut.mutate() : pauseMut.mutate()),
          toggleBusy: pauseMut.isPending || resumeMut.isPending,
          back: () => backMut.mutate(),
          next: () => skipMut.mutate(),
          stop: () => stopMut.mutate(),
          shuffle: () => shuffleMut.mutate(),
          loop: () => loopMut.mutate(),
          step: (delta) => {
            position.jumpTo(position.pos + delta);
            seekMut.mutate({ delta });
          },
          seek: (sec) => {
            position.jumpTo(sec);
            seekMut.mutate({ position: Math.round(sec) });
          },
        }
      : null;

  const volumeControl = (
    <VolumeControl
      value={localVolume}
      muted={!!latest?.muted}
      canMute={hasSession}
      onChange={setLocalVolume}
      onCommit={() => volumeMut.mutate(localVolume)}
      onToggleMute={() => muteMut.mutate(!latest?.muted)}
    />
  );
  const waiting = latest?.queue.slice(1) ?? [];
  const playHint = !canControl
    ? 'Abspielen braucht das Musik-Recht „bearbeiten“.'
    : !canPlay
      ? 'Erst oben einen Voice-Channel wählen.'
      : null;

  return (
    <div className="mx-auto max-w-5xl">
      <Link
        to={`/server/${guildId}`}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück zur Server-Übersicht
      </Link>

      {!canControl && (
        <div className="mb-4 flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide">nur ansehen</span>
          Abspielen und Steuern geht nicht — deine eigenen Playlists kannst du trotzdem verwalten.
        </div>
      )}

      {/* Everything that controls the bot — off without the edit right. */}
      <fieldset disabled={!canControl} className="m-0 min-w-0 border-0 p-0">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Music2 className="h-6 w-6 text-primary" /> Musik
          </h1>
          {/* relative z-20: the glass cards are stacking contexts, the
              suggestion list would otherwise slide under the next card. */}
          <div className="relative z-20 ml-auto w-full sm:w-96">
            <SongSearch guildId={guildId!} canPlay={canPlay} busy={playMut.isPending} onPlay={(q) => playMut.mutate(q)} />
          </div>
        </div>
        {!hasSession && (
          <div className="mb-4 max-w-md">
            <Label>Voice-Channel (wo der Bot joinen soll)</Label>
            <ChannelPicker guildId={guildId!} value={voiceChannelId} onChange={setVoiceChannelId} types={[CHANNEL_TYPE.GUILD_VOICE]} />
          </div>
        )}
        {playMut.isSuccess && !playMut.isPending && (
          <p className="mb-3 text-sm text-primary">Geschickt — erscheint gleich im Player bzw. in der Warteschlange.</p>
        )}
        {playMut.error && <p className="mb-3 text-sm text-destructive">Fehler: {(playMut.error as Error).message}</p>}
      </fieldset>

      <div role="tablist" className="mb-4 flex gap-1 overflow-x-auto overflow-y-hidden border-b">
        <TabButton active={tab === 'now'} onClick={() => setTab('now')}>
          Jetzt läuft
        </TabButton>
        <TabButton active={tab === 'queue'} onClick={() => setTab('queue')}>
          Warteschlange{waiting.length > 0 && <span className="ml-1.5 text-xs text-muted-foreground">{waiting.length}</span>}
        </TabButton>
        <TabButton active={tab === 'playlists'} onClick={() => setTab('playlists')}>
          Playlists
        </TabButton>
        <TabButton active={tab === 'radio'} onClick={() => setTab('radio')}>
          Radio
        </TabButton>
        <TabButton active={tab === 'history'} onClick={() => setTab('history')}>
          Verlauf
        </TabButton>
      </div>

      <div className="min-h-[18rem]">
        {tab === 'playlists' && (
          <>
            <PlaylistsTab
              guildId={guildId!}
              canPlay={canControl && canPlay}
              playHint={playHint}
              playing={playlistMut.isPending}
              onPlay={(id) => playlistMut.mutate(id)}
            />
            {playlistMut.error && <p className="mt-3 text-sm text-destructive">{(playlistMut.error as Error).message}</p>}
          </>
        )}

        {tab === 'history' && (
          <HistoryTab
            guildId={guildId!}
            canEdit={canControl}
            canPlay={canControl && canPlay}
            playHint={playHint}
            currentUid={current?.uid ?? null}
            onPlay={(url) => playMut.mutate(url)}
            onAddToPlaylist={setToPlaylist}
          />
        )}

        {/* Now / queue: controls only with the edit right, "Zur Playlist" also without. */}
        {tab === 'now' &&
          (controls && current ? (
            <NowPlaying
              state={controls.state}
              current={current}
              canControl={canControl}
              step={controls.step}
              onAddToPlaylist={() => setToPlaylist(fromQueueSong(current))}
            />
          ) : (
            <fieldset disabled={!canControl} className="m-0 min-w-0 border-0 p-0">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{latest ? 'Keine Wiedergabe' : 'Warte auf den Bot…'}</CardTitle>
                  <CardDescription>
                    Voice-Channel wählen und oben einen Song suchen — der Bot joint und spielt ab. Alternativ{' '}
                    <code>/play &lt;song&gt;</code> in Discord.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap items-center gap-3">
                    {volumeControl}
                    <span className="text-xs text-muted-foreground">gilt für alle Songs und das Radio</span>
                  </div>
                </CardContent>
              </Card>
            </fieldset>
          ))}

        {tab === 'queue' && (
          <QueueTab
            state={latest}
            canControl={canControl}
            busy={queueMut.isPending}
            onEdit={(edit) => queueMut.mutate(edit)}
            onAddToPlaylist={(s) => setToPlaylist(fromQueueSong(s))}
          />
        )}

        <fieldset disabled={!canControl} className="m-0 min-w-0 border-0 p-0">
          {tab === 'radio' && (
            <>
              <RadioTab
                guildId={guildId!}
                canEdit={canControl}
                canPlay={canControl && canPlay}
                playHint={playHint}
                playingId={playingStationId}
                startingId={radioStarting}
                onPlay={(id) => radioMut.mutate(id)}
              />
              {radioMut.error && <p className="mt-3 text-sm text-destructive">{(radioMut.error as Error).message}</p>}
            </>
          )}
        </fieldset>
      </div>

      {controls && current && (
        <fieldset disabled={!canControl} className="sticky bottom-0 z-30 m-0 min-w-0 border-0 p-0">
          <PlayerBar
            controls={controls}
            current={current}
            volume={volumeControl}
            error={controlError?.message ?? null}
            onOpen={() => setExpanded(true)}
            onShowNow={() => setTab('now')}
          />
        </fieldset>
      )}
      {expanded && controls && current && (
        <fieldset disabled={!canControl} className="m-0 min-w-0 border-0 p-0">
          <FullPlayer
            controls={controls}
            current={current}
            volume={volumeControl}
            onClose={() => setExpanded(false)}
            onShowQueue={() => {
              setExpanded(false);
              setTab('queue');
            }}
          />
        </fieldset>
      )}
      {toPlaylist && (
        <AddToPlaylistDialog guildId={guildId!} song={toPlaylist} onClose={() => setToPlaylist(null)} />
      )}
    </div>
  );
}

interface Controls {
  state: MusicState;
  pos: number;
  toggle: () => void;
  toggleBusy: boolean;
  back: () => void;
  next: () => void;
  stop: () => void;
  shuffle: () => void;
  loop: () => void;
  step: (delta: number) => void;
  seek: (sec: number) => void;
}

const hasNextSong = (s: MusicState) => s.queue.length > 1 || (s.loop === 'queue' && !s.isRadio);

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        '-mb-px shrink-0 whitespace-nowrap border-b-2 px-2.5 py-2 text-sm font-medium transition-colors sm:px-3',
        active ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
      )}
    >
      {children}
    </button>
  );
}

// --------------------------- Player bar ---------------------------

/** 🔀 ⏮ ▶ ⏭ 🔁 — the same row in the bar and the full player. */
function MainControls({ c, large }: { c: Controls; large?: boolean }) {
  const s = c.state;
  const loop = s.loop ?? 'off';
  const LoopIcon = loop === 'song' ? Repeat1 : Repeat;
  const icon = large ? 'h-6 w-6' : 'h-5 w-5';
  return (
    <div className={cn('flex items-center justify-center', large ? 'gap-4' : 'gap-1 sm:gap-2')}>
      <ModeButton label={s.shuffle ? 'Zufall: an' : 'Zufall: aus'} active={!!s.shuffle} disabled={s.isRadio} onClick={c.shuffle}>
        <Shuffle className={large ? 'h-5 w-5' : 'h-4 w-4'} />
      </ModeButton>
      <IconButton label="Zurück" disabled={s.isRadio} onClick={c.back}>
        <SkipBack className={cn(icon, 'fill-current')} />
      </IconButton>
      <button
        type="button"
        onClick={c.toggle}
        disabled={c.toggleBusy}
        aria-label={s.isPaused ? 'Weiter' : 'Pause'}
        title={s.isPaused ? 'Weiter' : 'Pause'}
        className={cn(
          'flex items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-transform hover:scale-105 active:scale-95 disabled:opacity-60',
          large ? 'h-16 w-16' : 'h-11 w-11'
        )}
      >
        {s.isPaused ? <Play className={cn(icon, 'ml-0.5 fill-current')} /> : <Pause className={cn(icon, 'fill-current')} />}
      </button>
      <IconButton label="Nächster Song" disabled={!hasNextSong(s)} onClick={c.next}>
        <SkipForward className={cn(icon, 'fill-current')} />
      </IconButton>
      <ModeButton label={LOOP_LABEL[loop]} active={loop !== 'off'} disabled={s.isRadio} onClick={c.loop}>
        <LoopIcon className={large ? 'h-5 w-5' : 'h-4 w-4'} />
      </ModeButton>
    </div>
  );
}

/** Shuffle / repeat: lit in the primary colour with a dot while on (like Spotify). */
function ModeButton({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <IconButton
      label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn('relative', active && 'text-primary hover:text-primary')}
    >
      {children}
      {active && <span className="absolute bottom-1 h-1 w-1 rounded-full bg-primary" />}
    </IconButton>
  );
}

function SongLine({ song, state, className }: { song: Song; state: MusicState; className?: string }) {
  return (
    <div className={cn('min-w-0', className)}>
      <div className="truncate text-sm font-semibold" title={song.title}>
        {song.title}
      </div>
      <div className="truncate text-xs text-muted-foreground">
        {song.source === 'spotify' && !state.isRadio && <SpotifyBadge />}
        {state.isPaused
          ? 'Pausiert'
          : state.isRadio
            ? 'Live'
            : [song.channel, song.requestedBy && `von ${song.requestedBy.username}`].filter(Boolean).join(' · ')}
      </div>
    </div>
  );
}

/**
 * Bar at the bottom of the page (sticky in the content's scroll area).
 * Desktop: song | controls + progress | stop + volume. Phones: mini bar —
 * a tap opens the full player.
 */
function PlayerBar({
  controls: c,
  current,
  volume,
  error,
  onOpen,
  onShowNow,
}: {
  controls: Controls;
  current: Song;
  volume: ReactNode;
  error: string | null;
  onOpen: () => void;
  onShowNow: () => void;
}) {
  const s = c.state;
  const pct = s.durationSec ? Math.min(100, (c.pos / s.durationSec) * 100) : 0;
  return (
    <div className="sticky bottom-0 z-30 mt-6 pb-1">
      <div className="glass-float overflow-hidden rounded-xl border bg-card shadow-xl">
        {/* Phones and narrow windows (the sidebars take a lot of width) */}
        <div className="lg:hidden">
          <div className="h-0.5 bg-muted">
            <div className="h-full bg-primary transition-[width]" style={{ width: `${s.isRadio ? 100 : pct}%` }} />
          </div>
          <div className="flex items-center gap-3 p-2">
            <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-3 text-left" aria-label="Player öffnen">
              <Cover src={current.thumbnail} radio={s.isRadio} className="h-11 w-11" />
              <SongLine song={current} state={s} className="flex-1" />
            </button>
            <IconButton label={s.isPaused ? 'Weiter' : 'Pause'} onClick={c.toggle} disabled={c.toggleBusy} className="text-foreground">
              {s.isPaused ? <Play className="h-5 w-5 fill-current" /> : <Pause className="h-5 w-5 fill-current" />}
            </IconButton>
            <IconButton label="Nächster Song" disabled={!hasNextSong(s)} onClick={c.next} className="text-foreground">
              <SkipForward className="h-5 w-5 fill-current" />
            </IconButton>
          </div>
        </div>

        {/* Desktop */}
        <div className="hidden grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)_minmax(0,1fr)] items-center gap-4 p-3 lg:grid">
          <button type="button" onClick={onShowNow} className="flex min-w-0 items-center gap-3 text-left" title="Jetzt läuft">
            <Cover src={current.thumbnail} radio={s.isRadio} className="h-14 w-14" />
            <SongLine song={current} state={s} />
          </button>
          <div className="min-w-0 space-y-1">
            <MainControls c={c} />
            <SeekBar state={s} pos={c.pos} onSeek={c.seek} compact />
          </div>
          <div className="flex min-w-0 items-center justify-end gap-1">
            <IconButton label="Stop" onClick={c.stop} className="hover:text-destructive">
              <Square className="h-4 w-4 fill-current" />
            </IconButton>
            {volume}
          </div>
        </div>
        {error && <p className="border-t px-3 py-1.5 text-xs text-destructive">Fehler: {error}</p>}
      </div>
    </div>
  );
}

/** Phones: the whole player on one screen (opened from the mini bar). */
function FullPlayer({
  controls: c,
  current,
  volume,
  onClose,
  onShowQueue,
}: {
  controls: Controls;
  current: Song;
  volume: ReactNode;
  onClose: () => void;
  onShowQueue: () => void;
}) {
  const s = c.state;
  return (
    <div className="glass-float fixed inset-0 z-50 flex flex-col overflow-y-auto bg-background/90 p-5">
      <div className="mb-6 flex items-center justify-between">
        <IconButton label="Player schließen" onClick={onClose}>
          <ChevronDown className="h-6 w-6" />
        </IconButton>
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {s.isRadio ? 'Live-Radio' : 'Spielt jetzt'}
        </span>
        <IconButton label="Warteschlange" onClick={onShowQueue}>
          <ListMusic className="h-5 w-5" />
        </IconButton>
      </div>
      {/* YouTube covers are 16:9 — a square crop would keep the letterbox bars. */}
      <Cover src={current.thumbnail} radio={s.isRadio} className="mx-auto aspect-video w-full max-w-md" />
      <SongLine song={current} state={s} className="mt-6 [&>div:first-child]:text-lg" />
      <div className="mt-4">
        <SeekBar state={s} pos={c.pos} onSeek={c.seek} />
      </div>
      <div className="mt-4">
        <MainControls c={c} large />
      </div>
      <div className="mt-4 flex items-center justify-center gap-6">
        <IconButton label={`${SEEK_STEP} s zurück`} disabled={!s.seekable} onClick={() => c.step(-SEEK_STEP)}>
          <Rewind className="h-5 w-5" />
        </IconButton>
        <IconButton label="Stop" onClick={c.stop} className="hover:text-destructive">
          <Square className="h-4 w-4 fill-current" />
        </IconButton>
        <IconButton label={`${SEEK_STEP} s vor`} disabled={!s.seekable} onClick={() => c.step(SEEK_STEP)}>
          <FastForward className="h-5 w-5" />
        </IconButton>
      </div>
      <div className="mt-6 flex justify-center">{volume}</div>
    </div>
  );
}

// --------------------------- Tabs ---------------------------

/** Big cover + song next to its lyrics; ±10 s live here on desktop. */
function NowPlaying({
  state,
  current,
  canControl,
  step,
  onAddToPlaylist,
}: {
  state: MusicState;
  current: Song;
  canControl: boolean;
  step: (delta: number) => void;
  onAddToPlaylist: () => void;
}) {
  return (
    // minmax(0, …): a long song title must not stretch the column past the screen.
    <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
      <div className="space-y-3">
        <Cover src={current.thumbnail} radio={state.isRadio} className="aspect-video w-full max-w-[340px]" />
        <div>
          <a
            href={current.url}
            target="_blank"
            rel="noreferrer"
            className="block truncate text-lg font-semibold hover:underline"
            title={current.title}
          >
            {current.title}
          </a>
          <div className="truncate text-sm text-muted-foreground">
            {[current.channel, current.requestedBy && `von ${current.requestedBy.username}`].filter(Boolean).join(' · ')}
          </div>
        </div>
        {!state.isRadio && (
          <div className="flex flex-wrap gap-2">
            {canControl && state.seekable && (
              <>
                <Button variant="outline" size="sm" className="hidden gap-1.5 md:inline-flex" onClick={() => step(-SEEK_STEP)}>
                  <Rewind className="h-4 w-4" /> {SEEK_STEP} s
                </Button>
                <Button variant="outline" size="sm" className="hidden gap-1.5 md:inline-flex" onClick={() => step(SEEK_STEP)}>
                  {SEEK_STEP} s <FastForward className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" className="gap-1.5" onClick={onAddToPlaylist}>
              <ListPlus className="h-4 w-4" /> Zur Playlist
            </Button>
          </div>
        )}
      </div>
      {state.isRadio ? (
        <Card className="self-start">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Live-Radio — kein Songtext, kein Spulen. Anderen Sender: Tab „Radio“.
          </CardContent>
        </Card>
      ) : (
        // Spotify titles come without the artist — Genius needs both.
        <LyricsPanel title={current.source === 'spotify' && current.channel ? `${current.title} ${current.channel}` : current.title} />
      )}
    </div>
  );
}

/**
 * The waiting songs: drag at the handle (mouse and finger) to reorder, ⋯ for
 * "Jetzt spielen" / "Als Nächstes" / "Entfernen". With shuffle on, the song
 * the bot drew as next is marked.
 */
function QueueTab({
  state,
  canControl,
  busy,
  onEdit,
  onAddToPlaylist,
}: {
  state: MusicState | null;
  canControl: boolean;
  busy: boolean;
  onEdit: (edit: QueueEdit) => void;
  onAddToPlaylist: (song: Song) => void;
}) {
  if (!state || state.queue.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6 text-sm text-muted-foreground">
          Die Warteschlange ist leer — oben einen Song suchen.
        </CardContent>
      </Card>
    );
  }
  const [current, ...waiting] = state.queue;
  const total = waiting.reduce((sum, s) => sum + (s.durationSec ?? 0), 0);
  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Läuft gerade</div>
        <div className="flex items-center gap-3 rounded-lg border bg-card p-2 text-sm ring-1 ring-primary/30">
          <Cover src={current.thumbnail} radio={state.isRadio} className="h-10 w-16" />
          <SongLine song={current} state={state} className="flex-1" />
          {current.durationSec != null && (
            <span className="shrink-0 font-mono text-xs text-muted-foreground">{formatTime(current.durationSec)}</span>
          )}
        </div>
      </div>
      <div>
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Als Nächstes {state.shuffle && '· Zufall an'}
            {state.loop === 'queue' && ' · Wiederholen'}
          </span>
          {waiting.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {waiting.length} {waiting.length === 1 ? 'Song' : 'Songs'}
              {total > 0 && ` · ${formatTime(total)}`}
            </span>
          )}
        </div>
        {waiting.length === 0 ? (
          <p className="rounded-lg border bg-card p-3 text-sm text-muted-foreground">Danach kommt nichts mehr.</p>
        ) : (
          <QueueList
            songs={waiting}
            nextUid={state.shuffle ? state.nextUid ?? null : null}
            canControl={canControl}
            busy={busy}
            onEdit={onEdit}
            onAddToPlaylist={onAddToPlaylist}
          />
        )}
      </div>
    </div>
  );
}

function QueueList({
  songs,
  nextUid,
  canControl,
  busy,
  onEdit,
  onAddToPlaylist,
}: {
  songs: Song[];
  nextUid: string | null;
  canControl: boolean;
  busy: boolean;
  onEdit: (edit: QueueEdit) => void;
  onAddToPlaylist: (song: Song) => void;
}) {
  // Shown until the bot's next state (with the real order) arrives.
  const [optimistic, setOptimistic] = useState<string[] | null>(null);
  useEffect(() => setOptimistic(null), [songs]);

  const byUid = new Map(songs.map((s) => [s.uid, s]));
  const shown = optimistic
    ? optimistic.map((uid) => byUid.get(uid)).filter((s): s is Song => !!s)
    : songs;

  const { listRef, draggingIndex, handle, lineAt } = useDragOrder(shown.length, (from, to) => {
    const order = shown.map((s) => s.uid);
    order.splice(to, 0, ...order.splice(from, 1));
    setOptimistic(order);
    // Queue index: 0 is the current song.
    onEdit({ op: 'move', uid: shown[from].uid, toIndex: to + 1 });
  });

  return (
    <ul ref={listRef} className="divide-y rounded-lg border bg-card">
      {shown.map((s, i) => {
        const line = lineAt(i);
        return (
          <li
            key={s.uid}
            className={cn('relative flex items-center gap-2 p-2 text-sm sm:gap-3', draggingIndex === i && 'bg-accent/60 opacity-60')}
          >
            {line && <DropLine position={line} />}
            {canControl && <DragHandle {...handle(i)} disabled={busy} />}
            <span className="hidden w-5 shrink-0 text-center font-mono text-xs text-muted-foreground sm:inline">{i + 1}</span>
            <Cover src={s.thumbnail} className="h-9 w-12 sm:h-10 sm:w-16" />
            <div className="min-w-0 flex-1">
              <a href={s.url} target="_blank" rel="noreferrer" className="block truncate hover:text-primary" title={s.title}>
                {s.title}
              </a>
              <div className="truncate text-xs text-muted-foreground">
                {nextUid === s.uid && <span className="mr-1.5 font-medium text-primary">Als Nächstes</span>}
                {s.source === 'spotify' && <SpotifyBadge />}
                {[s.channel, s.requestedBy && `von ${s.requestedBy.username}`].filter(Boolean).join(' · ')}
              </div>
            </div>
            {s.durationSec != null && (
              <span className="hidden shrink-0 font-mono text-xs text-muted-foreground sm:inline">{formatTime(s.durationSec)}</span>
            )}
            <ActionMenu
              trigger={
                <IconButton label="Mehr" className="h-8 w-8" disabled={busy}>
                  <MoreHorizontal className="h-4 w-4" />
                </IconButton>
              }
              items={[
                { key: 'jump', label: 'Jetzt spielen', icon: <Play className="h-4 w-4" />, onSelect: () => onEdit({ op: 'jump', uid: s.uid }) },
                { key: 'next', label: 'Als Nächstes', icon: <ListStart className="h-4 w-4" />, onSelect: () => onEdit({ op: 'next', uid: s.uid }) },
                { key: 'playlist', label: 'Zur Playlist hinzufügen', icon: <ListPlus className="h-4 w-4" />, onSelect: () => onAddToPlaylist(s) },
                { key: 'remove', label: 'Entfernen', icon: <Trash2 className="h-4 w-4" />, destructive: true, onSelect: () => onEdit({ op: 'remove', uid: s.uid }) },
              ].filter((item) => canControl || item.key === 'playlist')}
            />
          </li>
        );
      })}
    </ul>
  );
}

// --------------------------- Player parts ---------------------------

/** 🔊 (click = mute for the session) + short slider + %. */
function VolumeControl({
  value,
  muted,
  canMute,
  onChange,
  onCommit,
  onToggleMute,
}: {
  value: number;
  muted: boolean;
  canMute: boolean;
  onChange: (v: number) => void;
  onCommit: () => void;
  onToggleMute: () => void;
}) {
  const Icon = muted ? VolumeX : value < 50 ? Volume1 : Volume2;
  return (
    <div className="flex items-center gap-2">
      <IconButton
        label={muted ? 'Ton an' : canMute ? 'Stumm' : 'Lautstärke'}
        onClick={canMute ? onToggleMute : undefined}
        disabled={!canMute}
        className={cn('h-8 w-8', muted && 'text-destructive', !canMute && 'disabled:opacity-100')}
      >
        <Icon className="h-4 w-4" />
      </IconButton>
      <input
        type="range"
        min={1}
        max={200}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        onMouseUp={onCommit}
        onTouchEnd={onCommit}
        onKeyUp={onCommit}
        aria-label="Lautstärke"
        className={cn('range-player w-24 lg:w-32', muted && 'opacity-50')}
        style={{ '--pct': `${((value - 1) / 199) * 100}%` } as React.CSSProperties}
      />
      <span className="w-11 shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
        {muted ? 'stumm' : `${value}%`}
      </span>
    </div>
  );
}

/**
 * Where the current song is: runs on locally from the last state; a jump
 * shows its target until the bot's next state arrives (~1–2 s).
 */
function usePosition(state: MusicState | null, receivedAt: number) {
  const [now, setNow] = useState(() => Date.now());
  const playing = !!state?.playing;
  useEffect(() => {
    if (!playing) return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [playing]);

  const [pending, setPending] = useState<number | null>(null);
  useEffect(() => setPending(null), [state?.updatedAt]);

  const duration = state?.durationSec ?? 0;
  const base = state?.positionSec ?? 0;
  const running = playing ? base + Math.max(0, now - receivedAt) / 1000 : base;
  const pos = Math.min(duration || Infinity, Math.max(0, pending ?? running));
  return {
    pos,
    jumpTo: (sec: number) => setPending(Math.min(duration || Infinity, Math.max(0, sec))),
  };
}

/** Thin progress bar: click or drag to jump; the radio shows "Live". */
function SeekBar({
  state,
  pos,
  onSeek,
  compact,
}: {
  state: MusicState;
  pos: number;
  onSeek: (sec: number) => void;
  compact?: boolean;
}) {
  const [drag, setDrag] = useState<number | null>(null);

  if (state.isRadio || !state.seekable || !state.durationSec) {
    return (
      <p className={cn('text-center text-muted-foreground', compact ? 'text-[11px]' : 'text-sm')}>
        {state.isRadio ? 'Live-Stream — hier gibt es nichts zu spulen.' : 'Länge unbekannt — Spulen geht bei diesem Song nicht.'}
      </p>
    );
  }

  const duration = state.durationSec;
  const shown = drag ?? pos;
  const commit = () => {
    if (drag === null) return;
    onSeek(drag);
    setDrag(null);
  };
  const time = cn('shrink-0 font-mono tabular-nums text-muted-foreground', compact ? 'w-9 text-[11px]' : 'w-11 text-xs');

  return (
    <div className={cn('flex items-center', compact ? 'gap-2' : 'gap-3')}>
      <span className={cn(time, 'text-right')}>{formatTime(shown)}</span>
      <input
        type="range"
        min={0}
        max={duration}
        step={1}
        value={Math.round(shown)}
        onChange={(e) => setDrag(Number(e.target.value))}
        onMouseUp={commit}
        onTouchEnd={commit}
        onKeyUp={commit}
        aria-label="Position im Song"
        className="range-player flex-1"
        style={{ '--pct': `${(shown / duration) * 100}%` } as React.CSSProperties}
      />
      <span className={time}>{formatTime(duration)}</span>
    </div>
  );
}

// --------------------------- Lyrics ---------------------------

/** Song text next to the cover (Genius via /api/lyrics). */
function LyricsPanel({ title }: { title: string }) {
  const q = useQuery({
    queryKey: ['lyrics', title],
    queryFn: () =>
      api<{ title: string; artist: string | null; url: string | null; lyrics: string }>(
        `/api/lyrics?title=${encodeURIComponent(title)}`
      ),
    retry: false,
  });

  return (
    <Card className="flex max-h-[28rem] min-h-[12rem] flex-col overflow-hidden">
      <div className="border-b px-4 py-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Songtext</div>
        {q.data && (
          <div className="truncate text-sm">
            {q.data.title}
            {q.data.artist && <span className="text-muted-foreground"> · {q.data.artist}</span>}
          </div>
        )}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {q.isLoading && <p className="text-sm text-muted-foreground">Suche Songtext…</p>}
        {(q.error || (q.data && !q.data.lyrics)) && <p className="text-sm text-muted-foreground">Kein Songtext gefunden.</p>}
        {q.data?.lyrics && (
          <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed">{q.data.lyrics}</pre>
        )}
      </div>
      {q.data?.url && (
        <div className="border-t px-4 py-2 text-right text-xs">
          <a href={q.data.url} target="_blank" rel="noreferrer" className="text-primary hover:underline">
            Auf Genius öffnen ↗
          </a>
        </div>
      )}
    </Card>
  );
}
